import { ConfigRecord, DeviceTypes, ChemistryEdit, ConfigRecordFileNames, SanitizerTypes, HardwareConfig } from "../config";
import { State, findAllStates } from "../status";
import Locale from "../utils/Locale";
import { CoreParams } from "../types";
import { MissingCoreParamsError, Json } from "../utils";
import { Chemistry } from "../utils";
import { ChemicalType, ChemicalTypeSalt } from "../types"

export * as DesiredStateManager from "./desired-state-manager";

/**
 * Controller enums for BOW state: WaterQualityGraphItem
 */
enum WaterQualityGraphItem {
    AUTOFILL = "AUTOFILL",
    DISSOLVED_OXYGEN = "DISSOLVED_OXYGEN",
    FLOW_METER = "FLOW_METER",
    FLOW_SWITCH_PROCEDURE = "FLOW_SWITCH_PROCEDURE",
    FREE_CHLORINE = "FREE_CHLORINE",
    ORP = "ORP",
    PH = "PH",
    PRESSURE = "PRESSURE",
    SALT = "SALT",
    TOTAL_CHLORINE = "TOTAL_CHLORINE",
}

export function getWaterQualityFeatures(coreParams: CoreParams, bowUuid?: string ): {
    enablePh: boolean,
    enableOrp: boolean,
    enableSalt: boolean,
    enablePressure: boolean,
    enableRefill: boolean,
    enableFlowCheck: boolean,
    enableFreeChlorine: boolean,
    enableFlowMeter: boolean,
    enableTotalChlorine: boolean,
    enableDissolvedOxygenConcentration: boolean,
    enableDissolvedOxygenSaturation: boolean,
} {
    const configData = coreParams.configs?.find(item => item.fileName === ConfigRecordFileNames.HardwareConfig)?.data;
    const bodyOfWaterStates = [];
    configData?.BodiesOfWater?.forEach(item => {
        bodyOfWaterStates.push({
            states: findAllStates(item.UUID, coreParams.states),
            uuid: item.UUID
        });
    });

    let enablePh = false;
    let enableOrp = false;
    let enableSalt = false;
    let enablePressure = false;
    let enableRefill = false;
    let enableFlowCheck = false;
    let enableFreeChlorine = false;
    let enableTotalChlorine = false;
    let enableFlowMeter = false;
    let enableDissolvedOxygenConcentration = false;
    let enableDissolvedOxygenSaturation = false;

    if (bodyOfWaterStates.some(item => (!bowUuid || item.uuid === bowUuid) &&
        Object.keys(item.states).includes("WaterQualityGraphItems"))
    ) {
        bodyOfWaterStates.filter(item => (!bowUuid || item.uuid === bowUuid))
            .forEach(itemBodyOfWaterStates => {
                const WaterQualityGraphItems = (JSON.parse(itemBodyOfWaterStates.states.WaterQualityGraphItems || "[]") || []) as WaterQualityGraphItem[];
                WaterQualityGraphItems?.forEach(item => {
                    switch (item) {
                    case WaterQualityGraphItem.AUTOFILL: enableRefill = true; break;
                    case WaterQualityGraphItem.DISSOLVED_OXYGEN:
                        enableDissolvedOxygenConcentration = true;
                        enableDissolvedOxygenSaturation = true;
                    break;
                    case WaterQualityGraphItem.FLOW_METER: enableFlowMeter = true; break;
                    case WaterQualityGraphItem.FLOW_SWITCH_PROCEDURE: enableFlowCheck = true; break;
                    case WaterQualityGraphItem.FREE_CHLORINE: enableFreeChlorine = true; break;
                    case WaterQualityGraphItem.ORP: enableOrp = true; break;
                    case WaterQualityGraphItem.PH: enablePh = true; break;
                    case WaterQualityGraphItem.PRESSURE: enablePressure = true; break;
                    case WaterQualityGraphItem.SALT: enableSalt = true; break;
                    case WaterQualityGraphItem.TOTAL_CHLORINE: enableTotalChlorine = true; break;
                    }
                });
            })
        ;
    } else {
        const chemistryEditsRaw = coreParams.siteStates?.ChemistryEdits
        const chemistryEdits = JSON.parse(chemistryEditsRaw || "[]") as ChemistryEdit[];
        
        enablePh = chemistryEdits?.some(item => (!bowUuid || item.BodyOfWaterUUID === bowUuid) && (item.PhProbe));

        enableOrp = chemistryEdits?.some(item =>
            (!bowUuid || item.BodyOfWaterUUID === bowUuid)
            && (
                // Backwards compatibility
                item.ORPProbe
                || item.AvailableSanitizerTargetTypes?.includes(SanitizerTypes.ORP)
            )
        );
        

        enableSalt = chemistryEdits?.some(item => (!bowUuid || item.BodyOfWaterUUID === bowUuid) && item.SaltChlorine);

        enablePressure = configData?.Devices?.some(item => item.DeviceType === DeviceTypes.PRESSURESENSOR);

        enableRefill = bodyOfWaterStates.some(item => {
            if (bowUuid && item.uuid !== bowUuid) return;
            const autoFillEditSettings = JSON.parse(item.states?.AutoFillEditSettings || "{}");
            return autoFillEditSettings?.Enabled
        });

        enableFlowCheck = bodyOfWaterStates.some(itemBodyOfWaterStates => {
            if (bowUuid && itemBodyOfWaterStates.uuid !== bowUuid) return;
            const flowCheckEventSettings = JSON.parse(itemBodyOfWaterStates.FlowCheckEventSettings || "{}");
            return flowCheckEventSettings?.FlowCheckSettingsByPump?.some(item => item.SupportsFlowCheckEvents)
        });
        
        enableFreeChlorine = false;
        enableTotalChlorine = false;

        if (coreParams.siteStates?.AutoFilterVersion >= 32) {
            const availableSanitizerTargetTypes = [];
            chemistryEdits?.forEach(itemChemistryEdit => {
                if (bowUuid && itemChemistryEdit.BodyOfWaterUUID !== bowUuid) return;
                itemChemistryEdit.AvailableSanitizerTargetTypes?.forEach(item => {
                    if (!availableSanitizerTargetTypes.includes(item)) {
                        availableSanitizerTargetTypes.push(item);
                    }
                });
            });
            availableSanitizerTargetTypes.forEach(item => {
                switch (item) {
                case SanitizerTypes.FREE_CHLORINE: enableFreeChlorine = true; break;
                case SanitizerTypes.TOTAL_CHLORINE: enableTotalChlorine = true; break;
                }
            });
        }
    }

    return ({
        enableFlowCheck,
        enableFlowMeter,
        enableFreeChlorine,
        enableOrp,
        enablePh,
        enablePressure,
        enableRefill,
        enableSalt,
        enableTotalChlorine,
        enableDissolvedOxygenConcentration,
        enableDissolvedOxygenSaturation,
    });
}

/**
 * Accepts error object returned from the Controller for a Site.setDeviceState RPC request.
 * Returns:
 * - localized error message and
 * - determines if the error could be temporary or not.
 *  (Worth asking the user to rerty or just show the "please call support" text.)
 * @param error Error got from the Controller
 * @returns
 */
export function processSiteSetDeviceStateError(error: { message: string, code: string }): {
    msgForUser: string,
    retriable: boolean
} { 
    switch(error?.message) {
    /** @todo use error code once Controller supports it */
    case "Not available in current mode":
        return {
            msgForUser: Locale.gettext("Your Attendant has left Installer Mode. Please put it into Installer Mode."),
            retriable: true
        };
    case "Device is offline":
        return {
            msgForUser: Locale.gettext("Your Attendant is offline. Make sure it is turned on, booted up, has an internet connection, and is in Installer Mode."),
            retriable: true,
        }
    default:
        return {
            msgForUser: Locale.gettext('Error sending changes. Please check that the device is powered on and is online.'),
            retriable: false,
        }
    }
}

export function getChemistryEdits(coreParams: CoreParams): ChemistryEdit[] {
    if (!coreParams) throw new MissingCoreParamsError();
    const chemistryEdits = Json.safeJsonParse(
       coreParams.siteStates?.ChemistryEdits || "[]"
    ) ?? [];
    return chemistryEdits;
}

export function getChemistryEdit(coreParams: CoreParams, bowUuid: string): ChemistryEdit {
    if (!coreParams) throw new MissingCoreParamsError();
    const chemistryEdits = getChemistryEdits(coreParams);
    const chemistryEdit = chemistryEdits.find(
        item => item.BodyOfWaterUUID === bowUuid
    ) ?? {} as ChemistryEdit;
    return chemistryEdit;
}

/**
 * Determines whether dosing of at least one type of chemical is available for the given BOW.
 * @param coreParams 
 * @param [bowUuid]
 * @returns 
 */
export const areDosingItemsAvailable = function (coreParams: CoreParams, bowUuid?: string) {
    if (!coreParams) throw new MissingCoreParamsError();
    const chemistryEdits = getChemistryEdits(coreParams);
    const areDosingItemsAvailable = chemistryEdits?.some(item => (
        (!bowUuid || bowUuid === item.BodyOfWaterUUID)
        && (
            item.Acid
            || item?.Base
            || item?.LiquidChlorine
            || item?.CO2
            || item?.SaltChlorine
        )
    ));
    return areDosingItemsAvailable;
}

export function getOneTimeDosingChemicalTypeOptions(coreParams: CoreParams, bodyOfWaterUuid: string) {
    const options = [];
    const chemistryEdit = getChemistryEdit(coreParams, bodyOfWaterUuid);
    if (chemistryEdit.Acid) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.MuriaticAcid),
            value: ChemicalType.MuriaticAcid
        });
    }
    if (chemistryEdit.Base) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.Base),
            value: ChemicalType.Base
        });
    }
    if (chemistryEdit.CO2) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.Co2),
            value: ChemicalType.Co2
        });
    }
    if (chemistryEdit.LiquidChlorine) {
        options.push({
            label: Chemistry.getChemicalTypeName(ChemicalType.Chlorine),
            value: ChemicalType.Chlorine
        });
    }
    /*
    if (chemistryEdit.SaltChlorine) {
        options.push({
            label: Locale.gettext("Salt"),
            value: ChemicalTypeSalt
        });
    }
    */
    return options;
}

export function getOneTimeDosingChemicalUnits(coreParams: CoreParams, answers) {
    const chemistryEdit = getChemistryEdit(coreParams, answers.bodyOfWaterUuid);

    switch (answers.chemicalType) {
    case ChemicalType.MuriaticAcid:
        return chemistryEdit.AcidDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }));
    case ChemicalType.Base:
        return chemistryEdit.BaseDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    case ChemicalType.Co2:
        return chemistryEdit.CO2DosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    case ChemicalType.Chlorine:
        return chemistryEdit.LiquidChlorineDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    case ChemicalTypeSalt:
        return chemistryEdit.SaltDosingUnits.map(item => ({
            label: Chemistry.getChemicalDosingUnitName(item),
            value: item,
        }))
    }                
}

